import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { Conditional } from '@payaca/ambrosia';
import { requestGetDeal } from '@payaca/store/deals/dealsActions';
import { useDeal } from '@payaca/store/hooks/appState';
import {
  getHydratedPurchaseOrder,
  requestGetPurchaseOrder,
} from '@payaca/store/materialsList/materialsListActions';
import { HydratedPurchaseOrder } from '@payaca/types/materialsListTypes';
import { backOff } from 'exponential-backoff';
import QueryString from 'qs';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { PreviewDocumentSkeleton } from '../../components/PreviewQuoteInvoice/PreviewDocument';
import DirectPurchaseOrder from './components/DirectPurchaseOrder';
import IndirectPurchaseOrder from './components/IndirectPurchaseOrder';
import './PurchaseOrderPage.sass';
import PurchaseOrderPageSkeletonLoader from './PurchaseOrderPageSkeletonLoader';

type Props = {
  purchaseOrderId: number;
};

const PurchaseOrderPage: FC<Props> = ({
  purchaseOrderId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [purchaseOrder, setPurchaseOrder] = useState<HydratedPurchaseOrder>();

  const fetchPdfRetry = useCallback(() => {
    return backOff(
      async () => {
        return new Promise<void>((resolve, reject) => {
          dispatch(
            requestGetPurchaseOrder(purchaseOrderId, (po) => {
              if (po.pdfUrl) {
                resolve();
              } else {
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject();
              }
            })
          );
        });
      },
      {
        numOfAttempts: 5,
        startingDelay: 1500,
        delayFirstAttempt: true,
      }
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [purchaseOrderId]);

  useEffect(() => {
    if (!purchaseOrder?.pdfUrl) {
      void fetchPdfRetry();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'purchaseOrder.pdfUrl', and 'fetchPdfRetry'
  }, []);

  const { projectId, stockLocationId } = QueryString.parse(location.search, {
    ignoreQueryPrefix: true,
  }) as { projectId: string | undefined; stockLocationId: string | undefined };

  const deal = useDeal(projectId ? +projectId : undefined);

  const getPurchaseOrder = useCallback(() => {
    dispatch(
      getHydratedPurchaseOrder.request({
        purchaseOrderId,
        callback: (purchaseOrder) => {
          setPurchaseOrder(purchaseOrder);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', and 'purchaseOrderId'
  }, []);

  useEffect(() => {
    if (projectId) {
      dispatch(requestGetDeal(+projectId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [projectId]);

  useEffect(() => {
    getPurchaseOrder();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'getPurchaseOrder'
  }, []);

  let backTo = `/suppliers/${purchaseOrder?.supplierId}/purchase-orders`;
  if (stockLocationId) {
    backTo = `/stock-locations/${stockLocationId}/purchase-orders`;
  } else if (deal) {
    backTo = `/projects/${deal.publicId}/materials/purchase-orders`;
  }

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        {
          label: 'Back',
          to: backTo,
        },
        { label: 'Purchase order' },
      ]}
    >
      <div className="p-4">
        <Conditional condition={!purchaseOrder}>
          <PreviewDocumentSkeleton>
            <PurchaseOrderPageSkeletonLoader />
          </PreviewDocumentSkeleton>
        </Conditional>
        <Conditional condition={!!purchaseOrder}>
          {purchaseOrder?.metadata?.orderId ? (
            <DirectPurchaseOrder purchaseOrder={purchaseOrder} />
          ) : (
            <IndirectPurchaseOrder
              projectId={projectId ? +projectId : undefined}
              purchaseOrder={purchaseOrder}
              getPurchaseOrder={getPurchaseOrder}
              fetchPdfRetry={fetchPdfRetry}
            />
          )}
        </Conditional>
      </div>
    </AuthenticatedPageWrapper>
  );
};
export default PurchaseOrderPage;
